import { Link } from "react-router-dom";
import type { Lead } from "@/types/lead";

interface LeadCardProps {
  lead: Lead;
  variant?: "default" | "wide";
}

export default function LeadCard({ lead, variant = "default" }: LeadCardProps) {
  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 border-b border-background-200 pb-6 last:border-0 last:pb-0">
        <Link
          to={`/leads/${lead.id}`}
          className="sm:col-span-4 block w-full h-44 sm:h-36 rounded-lg overflow-hidden bg-background-200 cursor-pointer"
          aria-label={lead.title}
        >
          <img
            src={lead.cover}
            alt={`${lead.title} - ${lead.category}线索配图`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        </Link>
        <div className="sm:col-span-8 flex flex-col">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Link
              to={`/leads?category=${lead.categorySlug}`}
              className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 font-label hover:bg-secondary-200 transition-colors cursor-pointer"
            >
              {lead.category}
            </Link>
            <span className="text-xs text-foreground-500 font-label">{lead.date}</span>
          </div>
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 leading-snug">
            <Link to={`/leads/${lead.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {lead.title}
            </Link>
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{lead.summary}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500 font-label">
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              {lead.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              {lead.views.toLocaleString()} 阅读
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col h-full rounded-lg bg-background-50 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors">
      <Link
        to={`/leads/${lead.id}`}
        className="block w-full h-44 overflow-hidden bg-background-200 cursor-pointer"
        aria-label={lead.title}
      >
        <img
          src={lead.cover}
          alt={`${lead.title} - ${lead.category}线索配图`}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 mb-2.5">
          <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 font-label">
            {lead.category}
          </span>
          <span className="text-xs text-foreground-500 font-label">{lead.date}</span>
        </div>
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
          <Link to={`/leads/${lead.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {lead.title}
          </Link>
        </h3>
        <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">{lead.summary}</p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500 font-label">
          <span className="flex items-center gap-1">
            <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
            {lead.readTime}
          </span>
          <span className="flex items-center gap-1 text-primary-600">
            阅读全文
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </article>
  );
}